<template>
    <custom-table
        :title="$t('pages.cash_register.title')"
        :subTitle="$t('pages.cash_register.subTitle')"
        rowKey="id"
        :items="table.data"
        :loading="table.loading"
        :columns="fields"
        :actions="actions"
        :pagination="table.pagination"
        @action="handleClickAction"
        @changeTable="handleTableChange"
    >
        <template v-slot:name="{ row: record }">
            <div class="cash-name-cell">
                <span class="cash-name">{{ record.name }}</span>
                <span v-if="record.is_default" class="cash-default-pill">{{ $t('pages.cash_register.defaultBadge') }}</span>
            </div>
        </template>

        <template v-slot:netBalance="{ row: record }">
            <span class="cash-balance" :class="balanceClass(record.net_balance)">{{ formatTRY(record.net_balance) }}</span>
        </template>

        <template v-slot:income="{ row: record }">
            <span class="cash-balance-sub cash-balance--in">{{ formatTRY(record.transaction_income) }}</span>
        </template>

        <template v-slot:expense="{ row: record }">
            <span class="cash-balance-sub cash-balance--out">{{ formatTRY(record.transaction_expense) }}</span>
        </template>

        <template v-slot:manualIn="{ row: record }">
            <span class="cash-balance-sub cash-balance--in">{{ formatTRY(record.manual_in) }}</span>
        </template>

        <template v-slot:manualOut="{ row: record }">
            <span class="cash-balance-sub cash-balance--out">{{ formatTRY(record.manual_out) }}</span>
        </template>

        <template v-slot:actions="{ row: record }">
            <div class="d-flex justify-content-end">
                <el-tooltip :content="$t('common.detail')" placement="top">
                    <router-link :to="'/cash-register/detail/' + record.id" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1">
                        <span class="svg-icon svg-icon-3">
                            <inline-svg src="/media/icons/duotune/general/gen004.svg"/>
                        </span>
                    </router-link>
                </el-tooltip>
                <el-tooltip :content="$t('common.edit')" placement="top">
                    <a v-on:click="fetchCash(record)" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1">
                        <span class="svg-icon svg-icon-3">
                            <inline-svg src="/media/icons/duotune/art/art005.svg"/>
                        </span>
                    </a>
                </el-tooltip>
                <el-tooltip :content="canDelete ? $t('common.delete') : $t('pages.cash_register.cannotDeleteLast')" placement="top">
                    <el-popconfirm v-if="canDelete"
                        :title="$t('messages.sureDelete')"
                        :confirm-button-text="$t('btn.yes')"
                        :cancel-button-text="$t('btn.no')"
                        @confirm="deleteRecord([record.id])">
                        <template #reference>
                            <a class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm">
                                <span class="svg-icon svg-icon-3">
                                    <inline-svg src="/media/icons/duotune/general/gen027.svg"/>
                                </span>
                            </a>
                        </template>
                    </el-popconfirm>
                    <a v-else class="btn btn-icon btn-bg-light btn-sm cash-action--disabled">
                        <span class="svg-icon svg-icon-3">
                            <inline-svg src="/media/icons/duotune/general/gen027.svg"/>
                        </span>
                    </a>
                </el-tooltip>
            </div>
        </template>
    </custom-table>

    <div class="modal fade" id="kt_modal_cash" ref="cashModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered mw-560px">
            <div class="modal-content">
                <div class="modal-header">
                    <h2 class="fw-bolder">{{ form.title }}</h2>
                    <div data-bs-dismiss="modal" class="btn btn-icon btn-sm btn-active-icon-primary">
                        <span class="svg-icon svg-icon-1"><inline-svg src="/media/icons/duotune/arrows/arr061.svg"/></span>
                    </div>
                </div>
                <el-form @submit.prevent="onSubmit()" :model="form" ref="cashForm">
                    <div class="modal-body py-8 px-lg-10">
                        <div class="cash-form-field">
                            <label class="cash-form-label required">{{ $t('pages.cash_register.cols.name') }}</label>
                            <el-form-item prop="data.name" :rules="$validation.getMessage(['required'])">
                                <el-input v-model="form.data.name" type="text"/>
                            </el-form-item>
                        </div>

                        <div v-if="form.updateStatus" class="cash-default-toggle">
                            <label class="cash-toggle-wrap" :class="{'cash-toggle-wrap--disabled': defaultToggleDisabled}">
                                <input type="checkbox"
                                    v-model="form.data.is_default"
                                    :disabled="defaultToggleDisabled"/>
                                <span class="cash-toggle-pill"></span>
                                <span class="cash-toggle-text">
                                    <span class="cash-toggle-title">{{ $t('pages.cash_register.setAsDefault') }}</span>
                                    <span v-if="defaultToggleDisabled" class="cash-toggle-hint">{{ $t('pages.cash_register.defaultLocked') }}</span>
                                </span>
                            </label>
                        </div>
                    </div>

                    <div class="modal-footer flex-center">
                        <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal" :disabled="form.loading">
                            {{ $t('common.cancel') }}
                        </button>
                        <button :data-kt-indicator="form.loading ? 'on' : null" class="btn btn-primary" type="submit" :disabled="form.loading">
                            <span v-if="!form.loading" class="indicator-label">{{ $t("btn.save") }}</span>
                            <span v-if="form.loading" class="indicator-progress">
                                {{ $t("messages.wait") }}
                                <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                        </button>
                    </div>
                </el-form>
            </div>
        </div>
    </div>
</template>

<script>
import CustomTable from "@/components/custom-table";
import transactionService from "@/components/payment/transactionService";

export default {
    name: "cash-register",
    components: {
        CustomTable,
    },
    data() {
        return {
            fields: [
                { name: this.$t("pages.cash_register.cols.id"), key: "id", class: '' },
                { name: this.$t("pages.cash_register.cols.name"), scopedSlots: { customRender: "name" } },
                { name: this.$t("pages.cash_register.cols.netBalance"), scopedSlots: { customRender: "netBalance" } },
                { name: this.$t("pages.cash_register.cols.income"), scopedSlots: { customRender: "income" } },
                { name: this.$t("pages.cash_register.cols.expense"), scopedSlots: { customRender: "expense" } },
                { name: this.$t("pages.cash_register.cols.manualIn"), scopedSlots: { customRender: "manualIn" } },
                { name: this.$t("pages.cash_register.cols.manualOut"), scopedSlots: { customRender: "manualOut" } },
                { name: this.$t("common.action"), key: "action", scopedSlots: { customRender: "actions" } },
            ],
            actions: [
                { name: "new", icon: "bi-plus-lg", label: "btn.add" },
                { name: "refresh", icon: "bi-arrow-repeat", label: "btn.refresh" },
            ],
            form: {},
            filterParameters: {},
        }
    },
    computed: {
        table() {
            return this.$store.state.cash.table;
        },
        canDelete() {
            return (this.table.data || []).length > 1;
        },
        defaultToggleDisabled() {
            if (!this.form.updateStatus) return false;
            if (!this.form.data?.is_default) return false;
            return (this.table.data || []).length <= 1;
        },
    },
    created() {
        this.resetForm();
    },
    mounted() {
        this.setCurrentPageBreadcrumbs(this.$t("pages.cash_register.title"), []);
        this.refreshTable();
    },
    methods: {
        formatTRY(amount) {
            const n = parseFloat(amount);
            if (isNaN(n)) return '-';
            return n.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 });
        },
        balanceClass(amount) {
            const n = parseFloat(amount);
            if (isNaN(n) || n === 0) return 'cash-balance--neutral';
            return n > 0 ? 'cash-balance--positive' : 'cash-balance--negative';
        },
        onSubmit() {
            this.$refs.cashForm.validate((valid) => {
                if (!valid) return false;
                this.form.loading = true;
                const body = { name: this.form.data.name };
                if (this.form.updateStatus) body.is_default = !!this.form.data.is_default;

                const action = this.form.updateStatus
                    ? this.$store.dispatch('cash/update', { id: this.form.data.id, formData: body })
                    : this.$store.dispatch('cash/add', { formData: body });

                action.then(res => {
                    if (res?.status) {
                        this.onResponse(res, () => {
                            this.hideModal(this.$refs.cashModal);
                            this.refreshTable();
                            transactionService.refreshCashRegisters();
                        });
                    }
                    this.form.loading = false;
                });
            });
        },
        fetchCash(record) {
            this.form = {
                title: this.sprintf(this.$t("pages.cash_register.editCashPattern"), [record.name]),
                loading: false,
                updateStatus: true,
                data: {
                    id: record.id,
                    name: record.name,
                    is_default: !!record.is_default,
                },
            };
            this.showModal(this.$refs.cashModal);
        },
        newCash() {
            this.resetForm();
            this.form.title = this.$t("pages.cash_register.newCash");
            this.showModal(this.$refs.cashModal);
        },
        handleClickAction(name) {
            switch (name) {
                case "refresh":
                    this.refreshTable();
                    break;
                case "new":
                    this.newCash();
                    break;
                default:
                    break;
            }
        },
        handleTableChange(pagination, filterData) {
            this.filterParameters = filterData;
            this.$store.dispatch("cash/get", {
                page: pagination,
                filterData: this.filterParameters,
            });
        },
        deleteRecord(id) {
            this.$store.dispatch("cash/delete", { id }).then(() => {
                this.refreshTable();
                transactionService.refreshCashRegisters();
            });
        },
        refreshTable() {
            this.$store.dispatch('cash/get', {
                filterData: this.filterParameters,
            });
        },
        resetForm() {
            this.form = {
                title: '',
                loading: false,
                updateStatus: false,
                data: {
                    name: '',
                    is_default: false,
                },
            };
        },
    },
}
</script>

<style scoped>
.cash-name-cell {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 140px;
}

.cash-name {
    font-weight: 600;
}

.cash-default-pill {
    display: inline-flex;
    padding: 2px 8px;
    border-radius: 999px;
    background: rgba(var(--panel-accent-rgb, 0, 122, 255), 0.12);
    color: var(--panel-accent-text, #007AFF);
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}

.cash-balance {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    font-size: 13.5px;
}

.cash-balance--positive { color: #28a745; }
.cash-balance--negative { color: #ef4444; }
.cash-balance--neutral { color: #8e8e93; }

html.dark .cash-balance--positive,
html.midnight .cash-balance--positive { color: #34D399; }
html.dark .cash-balance--negative,
html.midnight .cash-balance--negative { color: #F87171; }
html.dark .cash-balance--neutral,
html.midnight .cash-balance--neutral { color: #9CA3AF; }

.cash-balance-sub {
    font-variant-numeric: tabular-nums;
    font-size: 12.5px;
    font-weight: 600;
}

.cash-balance--in { color: #28a745; }
.cash-balance--out { color: #ef4444; }

html.dark .cash-balance--in,
html.midnight .cash-balance--in { color: #34D399; }
html.dark .cash-balance--out,
html.midnight .cash-balance--out { color: #F87171; }

.cash-action--disabled {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
}


/* Form */
.cash-form-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 14px;
}

.cash-form-label {
    font-size: 12px;
    font-weight: 600;
    color: #1c1c1e;
}

.cash-form-label.required::after {
    content: ' *';
    color: #ef4444;
}

html.dark .cash-form-label,
html.midnight .cash-form-label { color: #EAE6E1; }

.cash-default-toggle {
    margin-top: 12px;
    padding: 12px 14px;
    border-radius: 10px;
    background: rgba(0, 0, 0, 0.025);
    border: 1px solid rgba(0, 0, 0, 0.06);
}

html.dark .cash-default-toggle,
html.midnight .cash-default-toggle {
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(255, 255, 255, 0.06);
}

.cash-toggle-wrap {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    user-select: none;
    width: 100%;
}

.cash-toggle-wrap--disabled { cursor: not-allowed; opacity: 0.7; }

.cash-toggle-wrap input { display: none; }

.cash-toggle-pill {
    flex-shrink: 0;
    width: 36px;
    height: 20px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.18);
    position: relative;
    transition: background 0.2s;
}

.cash-toggle-pill::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #fff;
    transition: transform 0.18s;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
}

.cash-toggle-wrap input:checked + .cash-toggle-pill {
    background: var(--panel-accent, #007AFF);
}

.cash-toggle-wrap input:checked + .cash-toggle-pill::after {
    transform: translateX(16px);
}

.cash-toggle-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.cash-toggle-title {
    font-size: 13px;
    font-weight: 600;
    color: #1c1c1e;
}

.cash-toggle-hint {
    font-size: 11px;
    color: #8e8e93;
}

html.dark .cash-toggle-title,
html.midnight .cash-toggle-title { color: #EAE6E1; }
html.dark .cash-toggle-hint,
html.midnight .cash-toggle-hint { color: #9A9CAE; }
</style>
